import { defu } from 'defu'
import type { MontillaLoadConfig } from '../types'
import type { MontillaContext } from '../types/destination'
import type { Destination } from '../types/mappings'
import { state, getFaro } from '../state'
import { FaroDestination } from '../destinations/faro/faro-destination'
import { getIdentityStore } from '../identity-store'
import { writeFaroUser } from './identify'
import { getUtmParams, isBot, isAutomated, isPwa } from '../browser'
import { getAcquisition, consumeAcquisitionRollover, ACQUISITION_UTM_KEYS } from '../stores/acquisition-store'
import { startNewFaroSession } from '../utils/faro-session'
import { getAdSessionAttrs } from './ads'
import { store } from '../store'
import { APP_KEY } from '../constants'
import { installPlugins } from '../plugins/install-plugins'
import type { MontillaPublicApi } from '../types/plugin'
import { loadMappings, MappingsRegistry, validateDestination } from '../utils/destinations/mappings'
import { derivePathTaxonomy } from '../utils/path-taxonomy'
import { resolveMappingsUrl, resolveClientConfigUrl } from '../constants'
import { loadClientConfig } from '../utils/client-config'
import type { ClientConfigResponse } from '../types/client-config'
import { setupAutoTrack, type AutoTrackOptions } from './auto-track'
import { getDistributionChannel } from '../channel'

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P]
}

const DEFAULT_CONFIG: DeepPartial<MontillaLoadConfig> = {
  app: {
    version: '0.0.0',
    environment: 'production',
  },
  track: {
    webVitals: true,
    location: true,
    logs: true,
    errors: true,
    session: true,
  },
}

/**
 * O middleware de recaptura do GA Client ID é registrado UMA vez por
 * runtime: `load()` é idempotente, mas um segundo registro empilharia uma
 * chamada por `page_view`.
 */
let gaClientIdMiddlewareRegistered = false

/** Reuse the snippet's parallel client-config prefetch if it fired one. */
function getPrefetchedClientConfig(): Promise<unknown> | undefined {
  return typeof window !== 'undefined'
    ? (window as unknown as { __MONTILLA_CONFIG_PROMISE__?: Promise<unknown> }).__MONTILLA_CONFIG_PROMISE__
    : undefined
}

/**
 * Map the remote client config's `sdk` block onto the `MontillaLoadConfig`
 * shape so it can be fed to `defu` as the BASE (inline config overrides it).
 * Only blocks present in the remote document are emitted, so `defu` never
 * clobbers inline/default values with `undefined`.
 */
function mapRemoteConfig(remote: ClientConfigResponse): DeepPartial<MontillaLoadConfig> {
  const sdk = remote.sdk
  if (!sdk) return {}
  const out: DeepPartial<MontillaLoadConfig> = {}
  if (sdk.track) out.track = { ...sdk.track }
  if (sdk.acquisition) out.acquisition = { resetOnNewUtm: sdk.acquisition.resetOnNewUtm }
  if (sdk.ads) out.ads = { ...sdk.ads }
  if (sdk.identity) {
    const cookie = sdk.identity.cookie
    out.identity = {
      ...(sdk.identity.persistence
        ? { persistence: sdk.identity.persistence as NonNullable<MontillaLoadConfig['identity']>['persistence'] }
        : {}),
      ...(cookie
        ? {
            cookie: {
              ...(cookie.maxAge !== undefined ? { maxAge: cookie.maxAge } : {}),
              ...(cookie.domain !== undefined ? { domain: cookie.domain } : {}),
              ...(cookie.sameSite ? { sameSite: cookie.sameSite as 'Strict' | 'Lax' | 'None' } : {}),
            },
          }
        : {}),
    }
  }
  if (sdk.cookieConsent) out.cookieConsent = { ...sdk.cookieConsent }
  if (sdk.autoTrack) out.autoTrack = { ...sdk.autoTrack } as AutoTrackOptions
  if (sdk.source) out.source = sdk.source as MontillaLoadConfig['source']
  // App identity may be supplied/overridden by the client config. `defu` keeps
  // inline `config.app` winning, so a consumer that passes `app.name` inline is
  // unaffected; one that omits it inherits the client's values (e.g. namespace).
  if (sdk.app) out.app = { ...sdk.app }
  return out
}

/**
 * Initialize the montilla.
 * @param config - The configuration object.
 */
export async function load(config: MontillaLoadConfig): Promise<void> {
  try {
    if (state.initialized) {
      return
    }
    state.initialized = true

    // ── Remote per-client config (opt-in via `config.clientKey`) ──
    // Fetch BEFORE building the merged config so app-affecting knobs (track.*,
    // acquisition, ads, identity, cookieConsent, autoTrack, source) can come
    // from it. The remote `sdk` block is the BASE; inline `config` OVERRIDES it.
    // No clientKey (or a failed fetch) → `remoteConfig` is null and behavior is
    // exactly as before. Reuses the snippet's parallel prefetch when present.
    // `config.clientConfig` curto-circuita a rede inteira: quem hospeda já
    // resolveu o documento (tipicamente no SSR, onde ele é buscado uma vez e
    // viaja no payload). É o mesmo dado por outra porta, então o resto do
    // caminho não muda.
    const env = config.app?.environment ?? 'production'
    const ssrClientConfig = config.clientConfig
    const remoteConfig = ssrClientConfig ?? await loadClientConfig({
      clientKey: config.clientKey,
      env,
      url: resolveClientConfigUrl(env),
      timeoutMs: config.mappings?.timeoutMs ?? 5000,
      prefetched: getPrefetchedClientConfig(),
    })
    // Só diagnóstico, para a extensão conseguir dizer por qual porta a config
    // entrou. Distinguir `fetch` de `none` exige olhar o RESULTADO da busca: sem
    // clientKey, ou com a rede falhando, `loadClientConfig` devolve null.
    state.clientConfigSource = ssrClientConfig ? 'ssr' : remoteConfig ? 'fetch' : 'none'
    const remoteBase = remoteConfig ? mapRemoteConfig(remoteConfig) : {}

    // defu makes the FIRST arg win: inline `config` beats `remoteBase` beats
    // `DEFAULT_CONFIG`. Backward compatible — with no remote config this is
    // identical to `defu(config, DEFAULT_CONFIG)`.
    const mergedConfig = defu(config, remoteBase, DEFAULT_CONFIG) as MontillaLoadConfig
    state.config = mergedConfig

    // Stamp event.domain source onto every dispatched event
    state.source = mergedConfig.source ?? 'browser'

    // Load destination mappings. When the remote client config carries
    // destinations, they feed the registry directly (preferred); otherwise the
    // legacy `?slugs=` fetch runs.
    await initMappings(mergedConfig, remoteConfig)

    // Resolve location if tracking enabled
    state.locationPromise ??= mergedConfig.track.location
      ? store.loadLocation()
      : Promise.resolve(null)

    const location = await state.locationPromise

    // Build context
    const identityStore = getIdentityStore()
    const identity = identityStore.getSnapshot()
    const url = new URL(window.location.href)
    const searchParams = new URLSearchParams(url.search)

    const context: MontillaContext = {
      app: {
        name: mergedConfig.app.name,
        version: mergedConfig.app.version,
        environment: mergedConfig.app.environment,
        namespace: mergedConfig.app.namespace,
      },
      page: {
        url: url.href,
        domain: url.hostname,
        path: url.pathname,
        queryString: url.search,
        referrer: document.referrer,
      },
      session: {
        is_bot: String(isBot(navigator.userAgent) || isAutomated()),
        is_pwa: String(isPwa()),
        // First-touch acquisition (referrer + utm_* + gclid) — frozen for the
        // visit, unless `acquisition.resetOnNewUtm` rolls it over on a new utm.
        ...getAcquisition(searchParams, { resetOnNewUtm: mergedConfig.acquisition?.resetOnNewUtm }),
        ...getAdSessionAttrs(searchParams),
        ...(location ? Object.fromEntries(Object.entries(location).map(([k, v]) => [k, String(v)])) : {}),
      },
      user: {
        anonymousId: identity.anonymousId,
        hash: identity.anonymousId,
        ...(identity.userId && { id: identity.userId }),
        ...(identity.traits.email ? { email: String(identity.traits.email) } : {}),
        ...(identity.traits.phone ? { phone: String(identity.traits.phone) } : {}),
        traits: identity.traits,
      },
      sdk: {
        name: APP_KEY,
        version: __MONTILLA_SDK_VERSION__,
      },
      location: location,
    }

    state.context = context

    // ── Faro-first init ──
    // Faro is the canonical metadata store (faro.metas.value). Initialize it
    // BEFORE other destinations so they can read user/session/page/view from
    // Faro during their own init(). Locale (currency) is stamped here so
    // every downstream destination — and the canonical-view resolver — picks
    // it up via `metadata.session.attributes.currency`.
    const faroConfig = mergedConfig.destinations?.faro
    if (faroConfig?.enabled !== false) {
      const faroDest = new FaroDestination()
      state.eventBus.register(faroDest)
      await state.eventBus.initOne(faroDest, mergedConfig, context)
      applyInitialLocale(mergedConfig, identity)
      applyInitialPageMeta(url)
      applyInitialUserMeta(identity)
      // `resetOnNewUtm`: if the landing rolled the acquisition over (a live
      // prior session, new utm), start a NEW Faro session id so it isn't
      // stitched onto the previous session under the old campaign.
      if (consumeAcquisitionRollover()) {
        const utm: Record<string, string> = {}
        for (const key of ACQUISITION_UTM_KEYS) {
          if (context.session[key]) utm[key] = context.session[key]
        }
        startNewFaroSession(utm)
      }

      // ── GA Client ID (cookie `_ga`) ──
      // Mesmo problema do `insider_id`: o gtag grava o cookie DEPOIS de
      // carregar, então a leitura no boot normalmente não acha nada. Daí o
      // poll com backoff + recaptura em cada `page_view`, exatamente como a
      // captura do Insider. Precisa vir depois do Faro init porque stampa em
      // user metadata (`faro.api.setUser`). Fire-and-forget: nunca atrasa o
      // load, e sem GA na página simplesmente não stampa nada.
      const { pollGaClientId, captureGaClientId } = await import('../utils/ga-client-id')
      pollGaClientId()
      if (!gaClientIdMiddlewareRegistered) {
        state.eventBus.use((event, next) => {
          if (event.name === 'page_view') captureGaClientId()
          next()
        })
        gaClientIdMiddlewareRegistered = true
      }
    }

    // Register Extension destination (self-guards via shouldEnable check)
    const { ExtensionDestination } = await import('../destinations/extension/extension-destination')
    state.eventBus.register(new ExtensionDestination())

    // ── Insider: resolve outer (SDK) + inner (destination) config and wire up ──
    const { setupInsider } = await import('../integrations/insider/install')
    await setupInsider(mergedConfig)

    // ── GTM: listener bridge and/or the gtm_data_layer destination ──
    // Registers the destination when configured inline (`destinations.gtm`),
    // declared by the remote client config, or via the legacy
    // `gtm.mode: 'source'` alias. Listener mode wires the dataLayer→Montilla
    // bridge instead (mutually exclusive with the destination).
    if (mergedConfig.gtm?.mode || mergedConfig.destinations?.gtm || hasRegistryDestination('gtm_data_layer')) {
      const { setupGtm } = await import('../integrations/gtm/install')
      await setupGtm(mergedConfig)
    }

    // Install plugins before destinations are initialized so they can
    // register their own destinations/middlewares during install().
    if (mergedConfig.plugins?.length) {
      const { montilla: montillaApi } = await import('../index')
      const publicApi: MontillaPublicApi = {
        track: montillaApi.track as unknown as MontillaPublicApi['track'],
        page: montillaApi.page as unknown as MontillaPublicApi['page'],
        identify: montillaApi.identify as unknown as MontillaPublicApi['identify'],
        log: montillaApi.log as unknown as MontillaPublicApi['log'],
        error: montillaApi.error as unknown as MontillaPublicApi['error'],
        ready: montillaApi.ready as unknown as MontillaPublicApi['ready'],
        getContext: montillaApi.getContext as unknown as MontillaPublicApi['getContext'],
        use: montillaApi.use,
      }
      await installPlugins(
        mergedConfig.plugins,
        mergedConfig,
        state.eventBus,
        montillaApi as unknown as Record<string, unknown>,
        publicApi,
      )
    }

    // Initialize all destinations
    await state.eventBus.initAll(mergedConfig, context)

    // ── Auto-tracking (opt-in via `autoTrack`, inline or remote) ──
    // `setupAutoTrack` is a standalone export not otherwise called by load();
    // wire it here when enabled so remote config can turn it on. `true` uses
    // defaults, an object tunes it, `false`/absent opts out. load()'s
    // `state.initialized` guard makes this run at most once per page.
    //
    // ONLY for bare-core consumers. The CDN (`@montilla/cdn`) and Nuxt
    // (`@montilla/nuxt`) entrypoints own auto-tracking themselves (default-on,
    // with wrapper-specific tuning like Nuxt's `autoPage: false`) and call
    // `setDistributionChannel(...)` before `load()`. Installing here too would
    // bind a second set of document listeners → duplicate events. So skip when
    // a wrapper channel is active; the wrapper is the single installer.
    if (getDistributionChannel() === '@montilla/core') {
      const autoTrack = mergedConfig.autoTrack
      if (autoTrack === true) {
        setupAutoTrack({})
      } else if (autoTrack && typeof autoTrack === 'object') {
        setupAutoTrack(autoTrack)
      }
    }

    // ── MontillaAds boot scan (opt-in via config.ads.enabled) ──
    // Renders every `[data-montilla-slot]` slot. The render module is a
    // dynamic import inside `ads.scan()`, so pages without the feature (or
    // without slots) never load the ads code. Fire-and-forget: an ad must
    // never delay or break SDK initialization.
    //
    // This is the BOOT scan and it deliberately runs here, not from a framework
    // lifecycle hook: `load()` resolves before hydration, so a server-rendered
    // slot fills without waiting for the app to mount (an app that defers
    // hydration until user interaction would otherwise show no ad at all).
    // Slots that appear LATER — SPA navigation — are the wrapper's job; the SDK
    // never observes the DOM. `autoScan: false` hands even this scan to the host.
    if (mergedConfig.ads?.enabled === true && mergedConfig.ads.autoScan !== false) {
      const { ads: adsApi } = await import('./ads')
      void adsApi.scan()
    }

    // ── Cookie consent banner (opt-in via config.cookieConsent) ──
    // Dynamically imported so the banner/styles code stays out of the base
    // bundle unless a consumer configures it. Purely additive — does not gate
    // the destination init above.
    if (mergedConfig.cookieConsent && mergedConfig.cookieConsent.enabled !== false) {
      const { setupCookieConsent } = await import('../integrations/cookie-consent/install')
      const { montilla: montillaApi } = await import('../index')
      setupCookieConsent(
        mergedConfig,
        (event, payload) => {
          void montillaApi.track(event, payload)
        },
        (traits) => {
          void montillaApi.identify(traits)
        },
      )
    }
  } catch (error) {
    // A causa vai NA STRING, não como segundo argumento. Um `Error` não tem
    // propriedades enumeráveis, então quem serializa o console (o argos, no
    // nosso caso) registrava só `Error initializing Montilla: {}` — o log
    // existia, provava a falha, e não dizia nada sobre o motivo. Sem a mensagem
    // não há como distinguir storage bloqueado de chunk 404 pós-deploy.
    const cause = error instanceof Error
      ? `${error.name}: ${error.message}`
      : String(error)
    console.error(`Error initializing Montilla: ${cause}`, error)
    state.initialized = false
    state.config = null
    state.context = null
    // `initAll()` é a última linha do try e é o único lugar que abre o portão
    // do `dispatch`. Sem isto, uma falha aqui deixava TODO `track()` preso num
    // await que nunca resolvia — sem erro, sem log, sem timeout. Melhor deixar
    // o evento seguir para os destinos que subiram (ou para nenhum) do que
    // pendurar o chamador para sempre.
    state.eventBus.releaseReady()
  }
}

/**
 * Whether the mappings registry (already populated by `initMappings`) carries
 * a destination of the given type — used to decide if a destination's setup
 * chunk is worth importing when there's no inline config for it.
 */
function hasRegistryDestination(type: Destination['type']): boolean {
  const registry = state.mappings
  if (!registry) return false
  for (const slug of registry.slugs()) {
    if (registry.get(slug)?.type === type) return true
  }
  return false
}

/**
 * Collect slugs from destination configs and load mappings from rum-api.
 * Populates `state.mappings`.
 *
 * In `environment === 'test'`, the bundled JSONs shipped in
 * `src/assets/mappings/` are dynamically imported and used as a fallback
 * when the remote fetch fails — this is what allows unit / E2E tests to
 * exercise destinations without spinning up a fake server.
 *
 * In every other env (`development`, `homolog`, `production`) there is
 * **no** bundled fallback. If the remote response is missing a slug, the
 * destination boots inert (logged from each destination's init) — the
 * rum-api / admin-api is the canonical source of truth, and serving stale
 * bundled data in production has historically hidden mapping bugs.
 *
 * The bundled JSONs are also excluded from production builds because the
 * imports below are dynamic and only hit under `env === 'test'`.
 */
async function initMappings(
  config: MontillaLoadConfig,
  remoteConfig?: ClientConfigResponse | null,
): Promise<void> {
  // When the remote client config carries destinations, they ARE the registry
  // — same element shape as mappings.json. Validate each (dropping invalid
  // ones individually, like the mappings loader) and skip the `?slugs=` fetch.
  if (remoteConfig?.destinations?.length) {
    const validated: Destination[] = []
    for (const item of remoteConfig.destinations) {
      try {
        validated.push(validateDestination(item))
      } catch (err) {
        console.warn('[montilla] Dropping invalid destination from client config:', err)
      }
    }
    state.mappings = MappingsRegistry.from(validated)
    return
  }

  const env = config.app.environment ?? 'production'
  const slugs: string[] = []
  const bundled: Record<string, Destination> = {}
  const wantsBundled = env === 'test'

  // Resolve bundled fallbacks lazily — only paid in test env, so tree-shakers
  // can drop the JSONs from production bundles.
  const [bundledInsider, bundledGtm] = wantsBundled
    ? await Promise.all([
        import('../assets/mappings/insider.json', { with: { type: 'json' } }).then(m => m.default),
        import('../assets/mappings/gtm.json', { with: { type: 'json' } }).then(m => m.default),
      ])
    : [null, null]

  // Insider
  // `slug` here is the combined `${slug}-${version}` identifier — what the
  // SDK sends in `?slugs=` and what the MappingsRegistry uses as its key.
  // The bundled JSONs only carry the bare slug + version, so combine them
  // explicitly when keying `bundled` and pushing into `slugs`.
  // Partner identity may live in EITHER the outer `config.insider` block
  // (SDK install) or the inner `config.destinations.insider` block — same
  // fallback `setupInsider` applies. `initMappings` must honor it too,
  // otherwise a consumer that declares partner only in the outer block
  // (the documented single-declaration pattern) never gets its slug pushed,
  // the mappings fetch is skipped, and the destination boots inert.
  const insiderCfg = config.destinations?.insider
  const partnerName = config.insider?.partnerName ?? insiderCfg?.partnerName
  const partnerId = config.insider?.partnerId ?? insiderCfg?.partnerId
  if (insiderCfg && partnerName && partnerId) {
    const bundledKey = bundledInsider
      ? `${bundledInsider.slug}-${bundledInsider.version}`
      : 'insider-default-v1'
    const slug = insiderCfg.slug ?? bundledKey
    slugs.push(slug)
    if (bundledInsider) {
      bundled[bundledKey] = bundledInsider as unknown as Destination
    }
  }

  // GTM — inline destination block or the legacy `gtm.mode: 'source'` alias.
  // Listener mode alone doesn't need mappings (it only translates
  // dataLayer → Montilla events).
  const gtmCfg = config.destinations?.gtm
  if ((gtmCfg && gtmCfg.enabled !== false) || config.gtm?.mode === 'source') {
    const bundledKey = bundledGtm
      ? `${bundledGtm.slug}-${bundledGtm.version}`
      : 'gtm-default-v1'
    const slug = gtmCfg?.slug ?? config.gtm?.slug ?? bundledKey
    slugs.push(slug)
    if (bundledGtm) {
      bundled[bundledKey] = bundledGtm as unknown as Destination
    }
  }

  // The snippet may have fired the mappings fetch in parallel with the SDK
  // script download — pick it up if present.
  const prefetched = typeof window !== 'undefined'
    ? (window as unknown as { __MONTILLA_MAPPINGS_PROMISE__?: Promise<unknown> }).__MONTILLA_MAPPINGS_PROMISE__
    : undefined

  const responses = await loadMappings({
    url: resolveMappingsUrl(env, config.mappings?.url),
    slugs,
    bundled,
    timeoutMs: config.mappings?.timeoutMs ?? 5000,
    prefetched,
  })

  state.mappings = MappingsRegistry.from(responses)
}

/**
 * Stamps initial locale (currency for now) into Faro's session metadata so
 * every destination — and the canonical-view resolver — picks it up via
 * `metadata.session.attributes.currency`.
 *
 * Resolution order (first non-empty wins): identity trait → 'BRL' fallback.
 * A future `config.locale.currency` block can slot in between identity and
 * the literal default without changing call sites.
 */
function applyInitialLocale(
  _config: MontillaLoadConfig,
  identity: ReturnType<ReturnType<typeof getIdentityStore>['getSnapshot']>,
): void {
  const faro = getFaro()
  if (!faro) return

  const traitCurrency = typeof identity.traits.currency === 'string'
    ? identity.traits.currency.trim().toUpperCase()
    : undefined

  const currency = traitCurrency || 'BRL'

  faro.api.setSession(defu(
    { attributes: { currency } },
    faro.metas.value.session ?? {},
  ))
}

/**
 * Stamps `path_taxonomy` (the slash-joined URL path) into Faro page
 * attributes. Mappings reference `metadata.page.attributes.path_taxonomy`
 * instead of re-deriving from the URL in each destination.
 */
function applyInitialPageMeta(url: URL): void {
  const faro = getFaro()
  if (!faro) return
  faro.api.setPage(defu(
    {
      url: url.href,
      attributes: {
        domain: url.hostname,
        path: url.pathname,
        queryString: url.search,
        ...getUtmParams(url.searchParams),
        path_taxonomy: derivePathTaxonomy(url.pathname),
        // Per-page referrer (this document's referrer) — distinct from the
        // session's first-touch acquisition referrer.
        ...(document.referrer ? { referrer: document.referrer } : {}),
      },
    },
    faro.metas.value.page ?? {},
  ))
}

/**
 * Populates `faro.metas.value.user` from persisted identity (cookie +
 * localStorage). Runs ONCE at boot, before other destinations init.
 *
 * No-op when there's no persisted identity. No event is dispatched — each
 * destination is responsible for replaying its own user mapping if it wants
 * the user payload pushed (e.g. `InsiderDestination.injectIdentity` runs
 * the identify mapping against this freshly populated meta).
 */
function applyInitialUserMeta(
  identity: ReturnType<ReturnType<typeof getIdentityStore>['getSnapshot']>,
): void {
  if (!identity.userId && Object.keys(identity.traits).length === 0) return
  writeFaroUser({
    userId: identity.userId ?? undefined,
    email: identity.traits.email as string | undefined,
    phone: identity.traits.phone as string | undefined,
    traits: identity.traits as Record<string, string>,
  })
}
